// UGC reels rail, /india only.
//
// Five Aura AI creator videos shown as a poster rail; tapping one opens a
// player. Deliberately NOT a shop widget: no product tile, no price, no add to
// cart. The reels are social proof for the app, not merchandising.
//
// PERFORMANCE. The <video> is mounted only while the player is open, so the
// section costs five poster JPEGs (~85KB) on load rather than the 36MB of
// encoded video sitting in /public/ugc. The sources are H.264 720x1280 with
// +faststart, so playback starts before the file finishes downloading. Two of
// the masters were HEVC, which Chrome and Firefox will not decode in a <video>
// tag, hence the transcode.

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconX, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import styles from "./UgcReels.module.css";

type Reel = { src: string; loop: string; poster: string; alt: string };

const REELS: Reel[] = [
  {
    src: "/ugc/reel-1.mp4",
    loop: "/ugc/loop-1.mp4",
    poster: "/ugc/reel-1.jpg",
    alt: "Aura AI user talking about balancing her mind",
  },
  {
    src: "/ugc/reel-2.mp4",
    loop: "/ugc/loop-2.mp4",
    poster: "/ugc/reel-2.jpg",
    alt: "Aura AI user sharing what the reading told her",
  },
  {
    src: "/ugc/reel-3.mp4",
    loop: "/ugc/loop-3.mp4",
    poster: "/ugc/reel-3.jpg",
    alt: "Aura AI user on how the guidance helped",
  },
  {
    src: "/ugc/reel-4.mp4",
    loop: "/ugc/loop-4.mp4",
    poster: "/ugc/reel-4.jpg",
    alt: "Aura AI user testimonial",
  },
  {
    src: "/ugc/reel-5.mp4",
    loop: "/ugc/loop-5.mp4",
    poster: "/ugc/reel-5.jpg",
    alt: "Aura AI user describing his experience",
  },
];

export default function UgcReels() {
  // null = closed. The full-size <video> only exists while this is a number.
  const [open, setOpen] = useState<number | null>(null);
  const previewRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const close = useCallback(() => setOpen(null), []);
  const step = useCallback(
    (delta: number) =>
      setOpen((i) =>
        i === null ? i : Math.min(REELS.length - 1, Math.max(0, i + delta)),
      ),
    [],
  );

  // The loops run on the native autoPlay/loop/muted attributes. An
  // IntersectionObserver was tried here to pause offscreen previews and was
  // removed: it paused them on first observation (the rail starts below the
  // fold) and then never reliably resumed, so the rail sat frozen. Browsers
  // already throttle decoding for offscreen media, and the five clips total
  // 528KB, so the observer was buying nothing for real risk.
  //
  // The one case still worth handling explicitly: mute the rail's competition
  // with the full-size clip while the player is open.
  useEffect(() => {
    const vids = previewRefs.current.filter(Boolean) as HTMLVideoElement[];
    if (!vids.length) return;
    if (open !== null) vids.forEach((v) => v.pause());
    else vids.forEach((v) => v.play().catch(() => {}));
  }, [open]);

  // Esc to close, arrows to move between reels, and lock the page behind the
  // player so the rail does not scroll underneath it on touch.
  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close, step]);

  return (
    <section className={styles.section} aria-labelledby="ugc-reels-heading">
      <div className={styles.head}>
        <h2 className={styles.title} id="ugc-reels-heading">
          Real people, real readings
        </h2>
        <p className={styles.sub}>
          What people across India say after asking Aura AI their first question.
        </p>
      </div>

      <div className={styles.rail}>
        {REELS.map((reel, i) => (
          <button
            key={reel.src}
            type="button"
            className={styles.card}
            onClick={() => setOpen(i)}
            aria-label={`Play video: ${reel.alt}`}
          >
            <video
              ref={(el) => {
                previewRefs.current[i] = el;
              }}
              className={styles.poster}
              src={reel.loop}
              poster={reel.poster}
              muted
              loop
              playsInline
              autoPlay
              preload="metadata"
              tabIndex={-1}
              aria-hidden="true"
            />
          </button>
        ))}
      </div>

      {/* Portalled to <body>. The section wrapper is position:relative with
          z-index:1, which opens a stacking context - inside it the player could
          never rise above the page, let alone the navbar (z-index 10000000).
          Rendering into body escapes that entirely. */}
      {open !== null &&
        createPortal(
          <div
            className={styles.backdrop}
          role="dialog"
          aria-modal="true"
          aria-label="Aura AI user video"
            onClick={close}
          >
            <div className={styles.player} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={styles.close}
              onClick={close}
              aria-label="Close video"
            >
              <IconX size={20} />
            </button>

            <button
              type="button"
              className={`${styles.nav} ${styles.navPrev}`}
              onClick={() => step(-1)}
              disabled={open === 0}
              aria-label="Previous video"
            >
              <IconChevronLeft size={20} />
            </button>
            <button
              type="button"
              className={`${styles.nav} ${styles.navNext}`}
              onClick={() => step(1)}
              disabled={open === REELS.length - 1}
              aria-label="Next video"
            >
              <IconChevronRight size={20} />
            </button>

            {/* key forces a fresh element per reel, so stepping does not leave
                the previous clip's buffer or playhead behind. */}
            <video
              key={REELS[open].src}
              className={styles.video}
              src={REELS[open].src}
              poster={REELS[open].poster}
              controls
              autoPlay
              playsInline
                preload="none"
              />
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
}
